import Easing from "./Easing";
import Interpolation from "./Interpolation";
import Sequence from "./Sequence";
// import now from "./Now";
import { now } from "@zyzgroup/core-common";
import { mainGroup } from "./mainGroup";

import type { EasingFunction } from "./Easing";
import type { InterpolationFunction } from "./Interpolation";
import type Group from "./Group";

export type UnknownProps = Record<string, any>;

export class Tween<T extends UnknownProps> {
  private _id = Sequence.nextId();

  // 是否正在执行
  private _isPlaying = false;
  // 动画开始时间
  private _startTime = 0;
  // 动画延迟时间
  private _delayTime = 0;
  // 动画持续时间
  private _durationTime = 1000;

  // 是否暂停
  private _isPaused = false;
  // 暂停开始时间
  private _pauseStartTime = 0;

  // 动画重复次数初始值
  private _initialRepeat = 0;
  // 动画重复次数剩余值
  private _repeat = 0;
  // 动画重复间隔
  private _repeatDelayTime?: number;
  // 动画重复时是否倒置
  private _yoyo = false;
  // 是否倒置运行
  private _reversed = false;

  // 初始值
  private _valuesStart: UnknownProps = {};
  // 结束值
  private _valuesEnd: Record<string, number | string> = {};
  // reverse时的初始值
  private _valuesStartRepeat: UnknownProps = {};

  // 缓动函数
  private _easingFunction: EasingFunction = Easing.Linear.None;
  // 插值函数
  private _interpolationFunction: InterpolationFunction = Interpolation.Linear;

  // 链式动画数组
  private _chainedTweens: Array<Tween<any>> = [];
  // 链式动画是否已停止
  private _isChainStopped = false;

  // 动画开始hook
  private _onStartCallback?: (object: T) => void;
  // 动画开始hook是否已被执行
  private _onStartCallbackFired = false;
  // 动画每轮repeat开始hook
  private _onEveryStartCallback?: (object: T) => void;
  // 动画每轮repeat开始hook是否已被执行
  private _onEveryStartCallbackFired = false;
  // 动画更新hook
  private _onUpdateCallback?: (object: T, elapsed: number) => void;
  // 动画repeat hook
  private _onRepeatCallback?: (object: T) => void;
  // 动画完成hook
  private _onCompleteCallback?: (object: T) => void;
  // 动画结束hook
  private _onStopCallback?: (object: T) => void;
  // 是否强制结束动画
  private _goToEnd = false;

  constructor(private _object: T, private _group: Group | false = mainGroup) {}

  getId(): number {
    return this._id;
  }

  isPlaying(): boolean {
    return this._isPlaying;
  }

  isPaused(): boolean {
    return this._isPaused;
  }

  to(properties: UnknownProps, duration?: number): this {
    this._valuesEnd = Object.create(properties);
    if (duration !== undefined) {
      this._durationTime = duration;
    }
    return this;
  }

  duration(d = 1000): this {
    this._durationTime = d;
    return this;
  }

  start(time: number = now()): this {
    if (this._isPlaying) {
      return this;
    }
    this._group && this._group.add(this as Tween<UnknownProps>);
    this._repeat = this._initialRepeat;
    if (this._reversed) {
      this._reversed = false;
      for (const property in this._valuesStartRepeat) {
        this._swapEndStartRepeatValues(property);
        this._valuesStart[property] = this._valuesStartRepeat[property];
      }
    }
    this._isPlaying = true;
    this._isPaused = false;
    this._onStartCallbackFired = false;
    this._onEveryStartCallbackFired = false;
    this._isChainStopped = false;
    this._startTime = time;
    this._startTime += this._delayTime;
    this._setupProperties(
      this._object,
      this._valuesStart,
      this._valuesEnd,
      this._valuesStartRepeat
    );
    return this;
  }

  private _setupProperties(
    _object: UnknownProps,
    _valuesStart: UnknownProps,
    _valuesEnd: UnknownProps,
    _valuesStartRepeat: UnknownProps
  ): void {
    for (const property in _valuesEnd) {
      const startValue = _object[property];
      const startValueIsArray = Array.isArray(startValue);
      const propType = startValueIsArray ? "array" : typeof startValue;
      const isInterpolationList =
        !startValueIsArray && Array.isArray(_valuesEnd[property]);
      if (propType === "undefined" || propType === "function") {
        continue;
      }
      if (isInterpolationList) {
        let endValues = _valuesEnd[property] as Array<number | string>;
        if (endValues.length === 0) {
          continue;
        }
        endValues = endValues.map(
          this._handleRelativeValue.bind(this, startValue as number)
        );
        _valuesEnd[property] = [startValue].concat(endValues);
      }
      if (
        (propType === "object" || startValueIsArray) &&
        startValue &&
        !isInterpolationList
      ) {
        _valuesStart[property] = startValueIsArray ? [] : {};
        for (const prop in startValue as object) {
          _valuesStart[property][prop] = startValue[prop];
        }
        _valuesStartRepeat[property] = startValueIsArray ? [] : {}; // TODO? repeat nested values? And yoyo? And array values?
        this._setupProperties(
          startValue,
          _valuesStart[property],
          _valuesEnd[property],
          _valuesStartRepeat[property]
        );
      } else {
        if (typeof _valuesStart[property] === "undefined") {
          _valuesStart[property] = startValue;
        }
        if (!startValueIsArray) {
          _valuesStart[property] *= 1.0;
        }
        if (isInterpolationList) {
          _valuesStartRepeat[property] = _valuesEnd[property].slice().reverse();
        } else {
          _valuesStartRepeat[property] = _valuesStart[property] || 0;
        }
      }
    }
  }

  stop(): this {
    if (!this._isChainStopped) {
      this._isChainStopped = true;
      this.stopChainedTweens();
    }
    if (!this._isPlaying) {
      return this;
    }
    this._group && this._group.remove(this as Tween<UnknownProps>);
    this._isPlaying = false;
    this._isPaused = false;
    if (this._onStopCallback) {
      this._onStopCallback(this._object);
    }
    return this;
  }

  end(): this {
    this._goToEnd = true;
    this.update(Infinity);
    return this;
  }

  pause(time: number = now()): this {
    if (this._isPaused || !this._isPlaying) {
      return this;
    }
    this._isPaused = true;
    this._pauseStartTime = time;
    this._group && this._group.remove(this as Tween<UnknownProps>);
    return this;
  }

  resume(time: number = now()): this {
    if (!this._isPaused || !this._isPlaying) {
      return this;
    }
    this._isPaused = false;
    this._startTime += time - this._pauseStartTime;
    this._pauseStartTime = 0;
    this._group && this._group.add(this as Tween<UnknownProps>);
    return this;
  }

  stopChainedTweens(): this {
    for (
      let i = 0, numChainedTweens = this._chainedTweens.length;
      i < numChainedTweens;
      i++
    ) {
      this._chainedTweens[i].stop();
    }
    return this;
  }

  group(group = mainGroup): this {
    this._group = group;
    return this;
  }

  delay(amount = 0): this {
    this._delayTime = amount;
    return this;
  }

  repeat(times = 0): this {
    this._initialRepeat = times;
    this._repeat = times;
    return this;
  }

  repeatDelay(amount?: number): this {
    this._repeatDelayTime = amount;
    return this;
  }

  yoyo(yoyo = false): this {
    this._yoyo = yoyo;
    return this;
  }

  easing(easingFunction: EasingFunction = Easing.Linear.None): this {
    this._easingFunction = easingFunction;
    return this;
  }

  interpolation(
    interpolationFunction: InterpolationFunction = Interpolation.Linear
  ): this {
    this._interpolationFunction = interpolationFunction;
    return this;
  }

  chain(...tweens: Array<Tween<any>>): this {
    this._chainedTweens = tweens;
    return this;
  }

  onStart(callback?: (object: T) => void): this {
    this._onStartCallback = callback;
    return this;
  }

  onEveryStart(callback?: (object: T) => void): this {
    this._onEveryStartCallback = callback;
    return this;
  }

  onUpdate(callback?: (object: T, elapsed: number) => void): this {
    this._onUpdateCallback = callback;
    return this;
  }

  onRepeat(callback?: (object: T) => void): this {
    this._onRepeatCallback = callback;
    return this;
  }

  onComplete(callback?: (object: T) => void): this {
    this._onCompleteCallback = callback;
    return this;
  }

  onStop(callback?: (object: T) => void): this {
    this._onStopCallback = callback;
    return this;
  }

  /**
   * @returns true if the tween is still playing after the update, false otherwise
   */
  update(time = now(), autoStart = true): boolean {
    if (this._isPaused) return true;
    let property;
    let elapsed;
    const endTime = this._startTime + this._durationTime;
    if (!this._goToEnd && !this._isPlaying) {
      if (time > endTime) return false;
      if (autoStart) this.start(time);
    }
    this._goToEnd = false;
    if (time < this._startTime) {
      return true;
    }
    if (this._onStartCallbackFired === false) {
      if (this._onStartCallback) {
        this._onStartCallback(this._object);
      }
      this._onStartCallbackFired = true;
    }
    if (this._onEveryStartCallbackFired === false) {
      if (this._onEveryStartCallback) {
        this._onEveryStartCallback(this._object);
      }
      this._onEveryStartCallbackFired = true;
    }
    elapsed = (time - this._startTime) / this._durationTime;
    elapsed = this._durationTime === 0 || elapsed > 1 ? 1 : elapsed;
    const value = this._easingFunction(elapsed);
    this._updateProperties(
      this._object,
      this._valuesStart,
      this._valuesEnd,
      value
    );
    if (this._onUpdateCallback) {
      this._onUpdateCallback(this._object, elapsed);
    }
    if (elapsed === 1) {
      if (this._repeat > 0) {
        if (isFinite(this._repeat)) {
          this._repeat--;
        }
        for (property in this._valuesStartRepeat) {
          if (!this._yoyo && typeof this._valuesEnd[property] === "string") {
            this._valuesStartRepeat[property] =
              this._valuesStartRepeat[property] +
              parseFloat(this._valuesEnd[property] as string);
          }
          if (this._yoyo) {
            this._swapEndStartRepeatValues(property);
          }
          this._valuesStart[property] = this._valuesStartRepeat[property];
        }
        if (this._yoyo) {
          this._reversed = !this._reversed;
        }
        if (this._repeatDelayTime !== undefined) {
          this._startTime = time + this._repeatDelayTime;
        } else {
          this._startTime = time + this._delayTime;
        }
        if (this._onRepeatCallback) {
          this._onRepeatCallback(this._object);
        }
        this._onEveryStartCallbackFired = false;
        return true;
      } else {
        if (this._onCompleteCallback) {
          this._onCompleteCallback(this._object);
        }
        for (
          let i = 0, numChainedTweens = this._chainedTweens.length;
          i < numChainedTweens;
          i++
        ) {
          // Make the chained tweens start exactly at the time they should,
          // even if the `update()` method was called way past the duration of the tween
          this._chainedTweens[i].start(this._startTime + this._durationTime);
        }
        this._isPlaying = false;
        return false;
      }
    }
    return true;
  }

  private _updateProperties(
    _object: UnknownProps,
    _valuesStart: UnknownProps,
    _valuesEnd: UnknownProps,
    value: number
  ): void {
    for (const property in _valuesEnd) {
      if (_valuesStart[property] === undefined) {
        continue;
      }
      const start = _valuesStart[property] || 0;
      let end = _valuesEnd[property];
      const startIsArray = Array.isArray(_object[property]);
      const endIsArray = Array.isArray(end);
      const isInterpolationList = !startIsArray && endIsArray;
      if (isInterpolationList) {
        _object[property] = this._interpolationFunction(
          end as Array<number>,
          value
        );
      } else if (typeof end === "object" && end) {
        this._updateProperties(_object[property], start, end, value);
      } else {
        end = this._handleRelativeValue(
          start as number,
          end as number | string
        );
        if (typeof end === "number") {
          _object[property] = start + (end - start) * value;
        }
      }
    }
  }

  // Parses relative end values with start as base (e.g.: +10, -3)
  private _handleRelativeValue(start: number, end: number | string): number {
    if (typeof end !== "string") {
      return end;
    }
    if (end.charAt(0) === "+" || end.charAt(0) === "-") {
      return start + parseFloat(end);
    } else {
      return parseFloat(end);
    }
  }

  private _swapEndStartRepeatValues(property: string): void {
    const tmp = this._valuesStartRepeat[property];
    const endValue = this._valuesEnd[property];
    if (typeof endValue === "string") {
      this._valuesStartRepeat[property] =
        this._valuesStartRepeat[property] + parseFloat(endValue);
    } else {
      this._valuesStartRepeat[property] = endValue;
    }
    this._valuesEnd[property] = tmp;
  }
}

export default Tween;
